import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, X, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientsPage = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  const canEdit = user && (user.role === 'admin' || user.role === 'reception');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', email: '', address: '' });
    setEditingClient(null);
    setShowForm(false);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      phone: client.phone,
      email: client.email || '',
      address: client.address || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        // Update existing client
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success('Cliente actualizado exitosamente');
      } else {
        // Create new client
        await axios.post(`${API}/clients`, formData);
        toast.success('Cliente creado exitosamente');
      }
      resetForm();
      fetchClients();
    } catch (error) {
      logger.error('Error saving client:', error);
      toast.error(editingClient ? 'Error al actualizar cliente' : 'Error al crear cliente');
    }
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.phone.includes(search) ||
    (client.email && client.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Clientes</h1>
          <Button
            onClick={() => {
              if (showForm) {
                resetForm();
              } else {
                setShowForm(true);
              }
            }}
            data-testid="toggle-form-button"
            className="bg-primary hover:bg-primary/90"
          >
            {showForm ? <X size={20} className="mr-2" weight="bold" /> : <Plus size={20} className="mr-2" weight="bold" />}
            {showForm ? 'Cancelar' : 'Nuevo Cliente'}
          </Button>
        </div>

        {showForm && (
          <div className="p-8 border border-border bg-background">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6">
              {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="client-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em]">Nombre *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-[0.2em]">Teléfono *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="ejemplo@correo.com"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-xs font-bold uppercase tracking-[0.2em]">Dirección</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="address-input"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-client-button" className="bg-primary hover:bg-primary/90">
                  {editingClient ? 'Actualizar Cliente' : 'Guardar Cliente'}
                </Button>
                {editingClient && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={resetForm}
                    data-testid="cancel-edit-button"
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </form>
          </div>
        )}

        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar por nombre, teléfono o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
            className="pl-10 border-border"
          />
        </div>

        <div className="border border-border bg-background">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="bg-secondary">
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Nombre</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Teléfono</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Dirección</th>
                  {canEdit && (
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-[0.2em]">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-secondary/50" data-testid={`client-row-${client.id}`}>
                      <td className="px-6 py-4 font-semibold">{client.name}</td>
                      <td className="px-6 py-4 font-mono text-sm">{client.phone}</td>
                      <td className="px-6 py-4 text-sm">{client.email || '-'}</td>
                      <td className="px-6 py-4 text-sm">{client.address || '-'}</td>
                      {canEdit && (
                        <td className="px-6 py-4 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(client)}
                            data-testid={`edit-client-${client.id}`}
                            className="hover:bg-primary hover:text-primary-foreground"
                          >
                            <PencilSimple size={16} className="mr-1" />
                            Editar
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canEdit ? 5 : 4} className="px-6 py-12 text-center text-muted-foreground">
                      No se encontraron clientes
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};
